<template>
  <div>
    <organism-idc-management
      v-if="filterData"
      :filter-list="filterData"
      :contract-amount="contractAmount"
      :customer-name-option="customerNameOption"
      :location-option="locationOption"
      :status-option="statusOption"
      :sale-contact-option="saleContactOption"
      :service-and-package-name-option="serviceAndPackageNameOption"
      :items="contractList"
      @clickRow="clickRow"
      @onUpdateFilterData="onUpdateFilterData"
      @onUpdateCurrentPageOrPerPage="onUpdateCurrentPageOrPerPage"
      @onSetDefaultFilterData="onSetDefaultFilterData"
    ></organism-idc-management>
  </div>
</template>

<script>
import OrganismIdcManagement from "@/components/organisms/portal/internal/OrganismIdcManagement.vue"
import mixins from "@/mixins"
import {
  getContractList,
  getLocationList,
  getServiceAndPackageNameList,
  getCustomerNameList,
  getSaleNameList,
  getContractStatusCount,
} from "@/api/api-module1"
import { mapActions, mapGetters } from "vuex"
import mixinsValidator from "@/mixins/mixinsValidator"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import mixinsSiteHelper from "@/mixins/mixinsSiteHelper"

export default {
  name: "IdcManagement",
  components: { OrganismIdcManagement },
  mixins: [mixins, mixinsValidator, mixinsObjectHelper, mixinsSiteHelper],
  data() {
    return {
      serviceAndPackageNameOption: [],
      customerNameOption: [],
      locationOption: [],
      statusOption: [
        { id: 1, name: "Approved" },
        { id: 2, name: "Deleted" },
        { id: 3, name: "Pending" },
        { id: 4, name: "Expired" },
        { id: 5, name: "Suspended" },
      ],
      saleContactOption: [],
      contractList: [],
      filterData: null,
      contractAmount: {
        all: 0,
        pending: 0,
        approved: 0,
        suspended: 0,
        expired: 0,
      },
    }
  },
  computed: {
    ...mapGetters("appFilter", ["getInternalIdcContractFilter"]),

    ...mapGetters("siteStore", ["siteSetting", "siteOptions"]),
  },
  watch: {
    async siteSetting() {
      await this.getContractListFromApi()
    },
  },
  async mounted() {
    await this.getContractListFromApi()
    await this.getLocationListFromApi()
    await this.getSaleList()
    await this.getCustomerNameListFromApi()
    await this.getServiceAndPackageNameListFromApi()
  },

  methods: {
    ...mapActions("appFilter", ["setInternalIdcContractFilter"]),

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getInternalIdcContractFilter
      this.filterData = this.deepCopyObject(filterDataFromVuex)
    },

    async getContractListFromApi() {
      this.showLoading(true)
      this.cloneFilterDataFromVuex()
      const filterData = this.filterDecoration()
      await this.getContractAmount(filterData)
      await getContractList(filterData)
        .then(async (response) => {
          if (response) {
            this.filterData.totalCount = response.totalCount
            const updatedFilter = {
              ...this.getInternalIdcContractFilter,
              totalCount: response.totalCount,
            }
            this.setInternalIdcContractFilter(updatedFilter)
            this.contractList = response.data.map((contract) => {
              const siteName = contract.siteName ?? "Site Not Found"

              return {
                id: contract.contractId.toString().padStart(8, "0"),
                additionalIds: contract.additionalIds,
                customer: {
                  customerName: contract.customer?.customerName,
                  organizationName: contract.customer?.customerOrganizationName,
                },
                package: contract.packages.map((item) => ({
                  packageName: item.packageName,
                  service: item.serviceName,
                  domain: item.domain,
                  rack: item.oceanRacks,
                  locationName: item.locationName,
                })),
                site: siteName,
                status: this.statusOption.find(
                  (status) =>
                    status.name.toLowerCase() === contract.status.toLowerCase()
                ),
                saleContact: {
                  name:
                    contract.saleContact?.contactFirstname +
                    " " +
                    contract.saleContact?.contactLastname,
                  phone: contract.saleContact?.contactPhones,
                  email: contract.saleContact?.contactEmail,
                },
              }
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    filterDecoration() {
      const filter = {
        SiteIds: this.siteOptions.map((site) => site.value),
        PageSize: this.filterData.perPage,
        PageNumber: this.filterData.currentPage,
        OrderBy: "ContractId desc",
        CustomerName: this.filterData.customer?.customerName || undefined,
        IdcId: this.filterData.idcId.replace(/\D/g, "") || undefined,
        AdditionalId: this.filterData.additionalId || undefined,
        PackageIds: this.filterData.packageAndService.length
          ? this.filterData.packageAndService.map((item) => item.id)
          : undefined,
        Statuses: this.filterData.status.length
          ? this.filterData.status.map((item) => item.name.toUpperCase())
          : undefined,
        LocationIds: this.filterData.location.length
          ? this.filterData.location.map((item) => item.locationId)
          : undefined,
        Domain: this.filterData.domainName || undefined,
        ContactPointId:
          this.filterData.saleContact?.contactPointId || undefined,
      }
      return filter
    },

    async getContractAmount(filterData) {
      await getContractStatusCount(filterData).then((response) => {
        if (response) {
          const allStatusAmount = response.data
          this.contractAmount = {
            pending: this.getEachStatusContractAmount(
              allStatusAmount,
              "PENDING"
            ),
            approved: this.getEachStatusContractAmount(
              allStatusAmount,
              "APPROVED"
            ),
            suspended: this.getEachStatusContractAmount(
              allStatusAmount,
              "SUSPENDED"
            ),
            expired: this.getEachStatusContractAmount(
              allStatusAmount,
              "EXPIRED"
            ),
          }
          this.contractAmount.all = allStatusAmount.stats.reduce(
            (sum, item) =>
              item.name !== "DELETED" ? sum + item.count || 0 : sum + 0,
            0
          )
        }
      })
    },

    getEachStatusContractAmount(allStatusAmount, status) {
      return (
        allStatusAmount.stats.find((item) => item.name === status)?.count || 0
      )
    },

    // click filter
    onUpdateFilterData(filter) {
      filter.currentPage = 1
      this.setInternalIdcContractFilter(filter)
      this.getContractListFromApi()
    },

    onUpdateCurrentPageOrPerPage(filter) {
      const updatedFilter = {
        ...this.getInternalIdcContractFilter,
        currentPage: filter.currentPage,
        perPage: filter.perPage,
      }
      this.setInternalIdcContractFilter(updatedFilter)
      this.getContractListFromApi()
    },

    onSetDefaultFilterData() {
      const defaultFilter = {
        idcId: "",
        additionalId: "",
        customer: "",
        location: [],
        domainName: "",
        saleContact: null,
        packageAndService: [],
        status: [
          { id: 1, name: "Approved" },
          { id: 3, name: "Pending" },
        ],
        currentPage: 1,
        totalCount: 0,
        perPage: 10,
      }
      this.setInternalIdcContractFilter(defaultFilter)
      this.getContractListFromApi()
    },

    async getServiceAndPackageNameListFromApi() {
      await getServiceAndPackageNameList().then((response) => {
        if (response) {
          this.serviceAndPackageNameOption = response.data
        }
      })
    },

    async getSaleList() {
      await getSaleNameList().then((response) => {
        if (response) {
          this.saleContactOption = response
        }
      })
    },

    async getCustomerNameListFromApi() {
      await getCustomerNameList().then((response) => {
        if (response) {
          this.customerNameOption = response
        }
      })
    },

    async getLocationListFromApi() {
      await getLocationList().then((response) => {
        if (response) {
          this.locationOption = response.data.map((location) => {
            return {
              ...location,
              label: `${
                location.siteName ? location.siteName : "Site Not Found"
              } - ${location.locationName}`,
            }
          })
        }
      })
    },

    clickRow(record) {
      this.$router.push("/internal/web-portal/idc/contract/" + record.id)
    },
  },
}
</script>

<style lang="scss" scoped></style>
